import { useState, useEffect, useRef } from "react";

const NewProductParametersPanel = ({
    panelTitle,
    productKNumber,
    fields = [], // array of { name, label, value, type?, placeholder?, editable?, height? }
    onFieldChange, // function (name, value) => void
    addYesMaybeNoButtons = false,
    handleYes,
    handleMaybe,
    handleNo,
    isCandidate
}) => {

    const handleChange = (name, value) => {
        if (typeof onFieldChange === "function") {
            onFieldChange(name, value);
        }
    };

    // refs to the rendered read-only elements so we can detect truncation
    const elRefs = useRef({});
    const setRef = (name) => (el) => {
        if (el) elRefs.current[name] = el;
    };

    // map of fieldName -> boolean (is truncated)
    const [isTruncatedMap, setIsTruncatedMap] = useState({});

    // popup state to show full content when truncated item clicked
    const [popup, setPopup] = useState({ open: false, label: "", content: "" });

    // detect truncation after render and on window resize
    useEffect(() => {
        const updateTruncation = () => {
            const next = {};
            fields.forEach((f) => {
                const el = elRefs.current[f.name];
                if (!el) {
                    next[f.name] = false;
                    return;
                }
                // consider truncated if scrollHeight > clientHeight
                next[f.name] = el.scrollHeight > el.clientHeight + 1;
            });
            setIsTruncatedMap((prev) => {
                // avoid unnecessary state updates
                const same =
                    Object.keys(next).length === Object.keys(prev).length &&
                    Object.keys(next).every((k) => prev[k] === next[k]);
                return same ? prev : next;
            });
        };

        updateTruncation();
        window.addEventListener("resize", updateTruncation);
        return () => window.removeEventListener("resize", updateTruncation);
    }, [fields]);

    const openPopup = (label, content) => setPopup({ open: true, label, content });
    const closePopup = () => setPopup({ open: false, label: "", content: "" });

    // local state to control button appearance and force redraw on click
    const [localCandidate, setLocalCandidate] = useState(isCandidate);
    useEffect(() => {
        // If isCandidate is not 'Yes', 'Maybe', or 'No', log a message to the console
        if (isCandidate && !["Yes", "Maybe", "No"].includes(isCandidate)) {
            console.log("Unexpected isCandidate value:", isCandidate);
        }
        setLocalCandidate(isCandidate);
    }, [isCandidate]);

    return (
        <div className="search-parameters-panel">
            <div className="feature-content-heading">{panelTitle}</div>

            {addYesMaybeNoButtons && (
                <div className="predicate-candidate-yes-maybe-no-section">
                    <div className="tech-char-label">Predicate candidate?</div>

                    <div className="yes-maybe-no-button-group">
                        <button
                            type="button"
                            className={localCandidate === "Yes" ? "yes-button" : "yes-button-inactive"}
                            onClick={() => {
                                setLocalCandidate("Yes");
                                if (typeof handleYes === "function") handleYes(productKNumber);
                            }}
                        >
                            Yes
                        </button>
                        <button
                            type="button"
                            className={localCandidate === "Maybe" ? "maybe-button" : "maybe-button-inactive"}
                            onClick={() => {
                                setLocalCandidate("Maybe");
                                if (typeof handleMaybe === "function") handleMaybe(productKNumber);
                            }}
                        >
                            Maybe
                        </button>
                        <button
                            type="button"
                            className={localCandidate === "No" ? "no-button" : "no-button-inactive"}
                            onClick={() => {
                                setLocalCandidate("No");
                                if (typeof handleNo === "function") handleNo(productKNumber);
                            }}
                        >
                            No
                        </button>
                    </div>
                </div>
            )}

            {fields.map((field) => {
                const {
                    name,
                    label,
                    value,
                    type = "text",
                    placeholder = "",
                    editable = false,
                    height // optional: if number -> treat as line count; if string -> CSS height (e.g. "60px")
                } = field;

                const isTextArea = type === "textarea";
                const isEditable = editable !== false;

                // style for truncated read-only content
                const readOnlyStyle = {};
                const readOnlyClampStyle = {};

                if (!isEditable && height !== undefined) {
                    if (typeof height === "number") {
                        // interpret as number of lines to show; using webkit line-clamp
                        readOnlyClampStyle.display = "-webkit-box";
                        readOnlyClampStyle.WebkitBoxOrient = "vertical";
                        readOnlyClampStyle.WebkitLineClamp = height;
                        readOnlyClampStyle.overflow = "hidden";
                        readOnlyClampStyle.cursor = "pointer";
                    } else if (typeof height === "string") {
                        readOnlyStyle.height = height;
                        readOnlyStyle.overflow = "hidden";
                        readOnlyStyle.cursor = "pointer";
                    }
                }

                const truncated = !isEditable && isTruncatedMap[name];

                return (
                    <div className="tech-char-label-group" key={name}>
                        <div className="tech-char-label">{label}</div>

                        {!isEditable ? (
                            <div
                                id={name}
                                ref={setRef(name)}
                                className="tech-char-readonly"
                                style={{ ...readOnlyStyle, ...readOnlyClampStyle }}
                                onClick={() => {
                                    if (truncated) openPopup(label, value);
                                }}
                                title={truncated ? "Click to view full text" : undefined}
                            >
                                {value}
                            </div>
                        ) : isTextArea ? (
                            <textarea
                                id={name}
                                className="tech-char-intended-use-textarea"
                                placeholder={placeholder}
                                value={value}
                                onChange={(e) => handleChange(name, e.target.value)}
                            />
                        ) : (
                            <input
                                id={name}
                                className="tech-char-input"
                                type={type}
                                placeholder={placeholder}
                                value={value}
                                onChange={(e) => handleChange(name, e.target.value)}
                            />
                        )}
                    </div>
                );
            })}

            {popup.open && (
                <div
                    className="popup-overlay"
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                    onClick={closePopup}
                >
                    <div
                        className="popup-box"
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#fff",
                            maxWidth: "40%",
                            maxHeight: "80%",
                            overflow: "auto",
                            padding: "16px",
                            borderRadius: "6px",
                            boxShadow: "0 4px 18px rgba(0,0,0,0.2)"
                        }}
                    >
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>{popup.label}</div>
                        <pre
                            style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                margin: 0,
                                fontFamily: "inherit"
                            }}
                        >
                            {popup.content}
                        </pre>

                        <div style={{ textAlign: "right", marginTop: 12 }}>
                            <button type="button" onClick={closePopup}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NewProductParametersPanel;